import { SxProps, Theme, styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  TextField,
  Select,
  FormControl,
  Autocomplete,
  Paper,
} from '@mui/material';
// Page Container
export const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
  },
}));

// Page Title
export const PageTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    fontSize: '1.5rem',
  },
}));

// Common button styles for form buttons (44px height)
export const commonButtonStyles: SxProps<Theme> = {
  borderRadius: '100px',
  height: '44px',
  fontSize: '16px',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

// Button styles for final action buttons (キャンセル / 入力内容確定) - keep 56px
export const finalActionButtonStyles: SxProps<Theme> = {
  borderRadius: '100px',
  height: '56px',
  fontSize: '16px',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

// Button styles for auto-fill address buttons - keep 56px
export const autoFillButtonStyles: SxProps<Theme> = {
  borderRadius: '100px',
  height: '56px',
  fontSize: '16px',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

// Shared label shrink styles for mobile
export const mobileLabelShrinkStyles = {
  top: 0,
  left: 0,
  transform: 'translate(14px, -9px) scale(0.75)',
};

// Shared label root styles for mobile
export const getMobileLabelRootStyles = (theme: Theme) => ({
  [theme.breakpoints.down('sm')]: {
    top: '50%',
    left: '14px',
    transform: 'translateY(-50%)',
    '&.MuiInputLabel-shrink': mobileLabelShrinkStyles,
  },
});

// Shared input base input styles for mobile
export const getMobileInputBaseInputStyles = (theme: Theme) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
});

// Global TextField component with consistent height and maxWidth
export const GlobalTextField = styled(TextField)(({ theme }) => ({
  maxWidth: '100%',
  '& .MuiInputBase-root': {
    height: '40px',
    [theme.breakpoints.down('sm')]: {
      height: '56px',
      display: 'flex',
      alignItems: 'center',
    },
  },
  '& .MuiInputBase-input': getMobileInputBaseInputStyles(theme),
  '& .MuiInputLabel-root': getMobileLabelRootStyles(theme),
}));

// Global Select component with consistent height and maxWidth
export const GlobalSelect = styled(Select)(({ theme }) => ({
  '& .MuiInputBase-root': {
    height: '40px',
    [theme.breakpoints.down('sm')]: {
      height: '56px',
    },
  },
  '& .MuiSelect-select': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

// Global FormControl component with consistent height and maxWidth
export const GlobalFormControl = styled(FormControl)(({ theme }) => ({
  maxWidth: '300px',
  '& .MuiInputBase-root': {
    height: '40px',
    [theme.breakpoints.down('sm')]: {
      height: '56px',
    },
  },
  '& .MuiSelect-select': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiInputLabel-root': getMobileLabelRootStyles(theme),
}));

// Global Autocomplete component with consistent height and maxWidth
export const GlobalAutocomplete = styled(Autocomplete)(({ theme }) => ({
  maxWidth: '100%',
  '& .MuiInputBase-root': {
    height: '40px',
    [theme.breakpoints.down('sm')]: {
      height: '56px',
      display: 'flex',
      alignItems: 'center',
    },
  },
  '& .MuiInputBase-input': getMobileInputBaseInputStyles(theme),
  '& .MuiInputLabel-root': getMobileLabelRootStyles(theme),
}));

// Common button styles for action buttons in pagination and tables
// Primary action button (Search, Archive, etc.)
export const primaryActionButtonStyles = (theme: Theme): SxProps<Theme> => ({
  borderRadius: '50px',
  padding: '8px 24px',
  height: '40px',
  textTransform: 'none',
  fontWeight: 500,
  minWidth: 120,
  border: `1px solid ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  '&:hover': {
    border: `1px solid ${theme.palette.primary.dark}`,
    backgroundColor: theme.palette.primary.dark,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    border: 'none',
  },
});

// Secondary action button (Clear, Return to Active, etc.)
export const secondaryActionButtonStyles = (theme: Theme): SxProps<Theme> => ({
  borderRadius: '50px',
  padding: '8px 24px',
  height: '40px',
  textTransform: 'none',
  fontWeight: 500,
  minWidth: 120,
  border: `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: 'transparent',
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.grey[400]}`,
  },
  '&:disabled': {
    border: `1px solid ${theme.palette.grey[200]}`,
    color: theme.palette.grey[400],
  },
});

export const responsivePrimaryButtonStyles = (
  theme: Theme,
  isDisabled?: boolean
): SxProps<Theme> => ({
  minWidth: '80px',
  height: '40px',
  px: { xs: 1, sm: 1.5, md: 2 },
  py: { xs: 0.5, sm: 0.75, md: 1 },
  borderRadius: '50px',
  color: isDisabled ? theme.palette.text.secondary : theme.palette.primary.main,
  border: '1px solid',
  borderColor: isDisabled ? theme.palette.text.secondary : theme.palette.primary.main,
  '&:hover': {
    backgroundColor: isDisabled ? 'transparent' : 'rgba(0, 78, 162, 0.1)',
    borderColor: isDisabled ? theme.palette.text.secondary : theme.palette.primary.main,
  },
  '&.Mui-disabled': {
    color: theme.palette.text.secondary,
    borderColor: '#C4C6D0',
  },
});

// Danger action button (Delete, etc.)
export const dangerActionButtonStyles = (theme: Theme): SxProps<Theme> => ({
  borderRadius: '50px',
  padding: '8px 24px',
  height: '40px',
  textTransform: 'none',
  fontWeight: 500,
  minWidth: 120,
  backgroundColor: '#FF1744',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#D50000',
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
});

export const MobileSectionLabelStyles = styled(Typography)({
  fontSize: '14px',
  color: '#74777F',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: '0.25px',
});

export const MobileSectionValueStyles = styled(Typography)({
  fontSize: '14px',
  color: '#0047BA',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: '0.25px',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
});

export const CalendarBoxStyles = styled(Box)({
  width: '300px',
  flexShrink: 0,
});

export const highlightedStyles = (): SxProps<Theme> => ({
  backgroundColor: '#FEF2D3  !important',
  '& .MuiInputBase-input': {
    '&:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #FEF2D3 inset !important',
      transition: 'background-color 5000s ease-in-out 0s',
    },
  },
});

export const PaperBoxStyles = styled(Paper)({
  paddingTop: '24px',
  paddingLeft: '24px',
  paddingRight: '24px',
});
